<script>
  import { onMount, afterUpdate } from 'svelte';
  import { imageStore, editStore, settingsStore } from '../stores';
  import { handleZoom, canvasToBase64, createMaskImage } from '../utils/imageUtils';
  import { constructUrl } from '../utils/apiClient';

  export let showZoom = true;
  export let hideLeftBorder = false;

  let canvas;
  let ctx;
  let maskCanvas;
  let maskCtx;
  let isDrawing = false;
  let lastX = 0;
  let lastY = 0;
  let canvasContainer;
  let zoomSliderValue = 100; // 확대/축소 슬라이더 초기값 (100% = 기본 크기)
  let userAdjustedZoom = false; // 사용자가 줌을 조정했는지 여부
  let isDragging = false; // 이미지 드래그 상태
  let dragStartX = 0;
  let dragStartY = 0;
  
  // 캔버스 모드 여부 (인페인팅 또는 객체 지우기 모드인지)
  $: canvasMode = $editStore.selectedFeature && 
               ($editStore.selectedFeature.id === 'inpaint' || 
                $editStore.selectedFeature.id === 'erase_object');
  
  // 아웃페인트 모드 여부
  $: outpaintMode = $editStore.selectedFeature &&
                 $editStore.selectedFeature.id === 'outpaint';
  $: displayedImageUrl = $imageStore.resultImage || $imageStore.uploadedImageUrl || $imageStore.serverImageUrl;
  $: isVideoResult = Boolean(displayedImageUrl && displayedImageUrl.split('?')[0].toLowerCase().endsWith('.mp4'));
  
  // 브러시 설정
  $: brushSize = $settingsStore.brushSize;
  $: brushColor = $settingsStore.brushColor;
  
  // 이미지 로드 시 캔버스 초기화
  $: if (
    $imageStore.uploadedImageUrl &&
    (canvasMode || outpaintMode) &&
    canvas &&
    (!canvasMode || maskCanvas)
  ) {
    initCanvas();
  }

  // 기능 변경 시 마스크 캔버스 초기화
  $: if (!canvasMode && maskCtx && maskCanvas) {
    maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
  }
  
  // 줌 레벨 변경 시 캔버스 업데이트
  $: if ($settingsStore.zoomLevel && ctx && !$editStore.isLoading) {
    if (canvasMode || outpaintMode) {
      redrawCanvas();
    }
  }
  
  // 브러시 설정 변경 시 업데이트
  $: if (brushSize && ctx) {
    ctx.lineWidth = brushSize;
  }
  $: if (brushSize && maskCtx) {
    maskCtx.lineWidth = brushSize;
  }
  $: if (brushColor && maskCtx) {
    maskCtx.strokeStyle = brushColor;
    maskCtx.fillStyle = brushColor;
  }

  // 줌 슬라이더 값이 변경될 때 줌 레벨 업데이트
  $: if (zoomSliderValue) {
    $settingsStore.zoomLevel = zoomSliderValue;
  }
  
  // 해상도 증가 후 이미지 크기 업데이트 감지
  $: if ($imageStore.resultImage && !$editStore.isLoading) {
    updateImageDimensions($imageStore.resultImage);
  }
  
  // 아웃페인트 설정 변경 시 캔버스 업데이트
  $: if (outpaintMode && ctx && $settingsStore.outpaintSettings && !$editStore.isLoading) {
    redrawCanvas();
  }
  
  // 이미지 크기 정보 업데이트 횟수를 제한하기 위한 변수들
  let updateCount = 0;
  let maxUpdates = 5;
  let isUpdating = false;
  
  function updateImageDimensions(imageUrl) {
    if (!imageUrl || isUpdating) return;
    
    // 업데이트 횟수가 최대치에 도달하면 더 이상 업데이트하지 않음
    if (updateCount >= maxUpdates) {
      console.log("이미지 크기 정보 업데이트 최대 횟수 도달");
      return;
    }
    
    isUpdating = true;
    const img = new Image();
    img.onload = () => {
      $imageStore.imageWidth = img.width;
      $imageStore.imageHeight = img.height;
      console.log("이미지 크기 정보 업데이트:", img.width, "x", img.height, "업데이트 횟수:", updateCount + 1);
      updateCount++;
      isUpdating = false;
    };
    img.onerror = (e) => {
      console.error("이미지 로드 오류:", e);
      isUpdating = false;
    };
    img.src = imageUrl;
  }
  
  // 사용자 클릭 이벤트 발생 시 업데이트 카운터 초기화
  function resetUpdateCounter() {
    updateCount = 0;
    console.log("이미지 크기 정보 업데이트 카운터 초기화");
  }
  
  function zoomIn() {
    zoomSliderValue = zoomSliderValue + 1;
    $settingsStore.zoomLevel = zoomSliderValue;
    userAdjustedZoom = true;
  }

  function zoomOut() {
    zoomSliderValue =  zoomSliderValue - 1;
    $settingsStore.zoomLevel = zoomSliderValue;
    userAdjustedZoom = true;
  }
  
  function initCanvas() {
    if (!canvas) return;
    userAdjustedZoom = false;
    
    const img = new Image();
    img.crossOrigin = "Anonymous";
    
    img.onload = () => {
      console.log("캔버스 이미지 로드 성공");
      $imageStore.imageWidth = img.width;
      $imageStore.imageHeight = img.height;

      if (outpaintMode) {
        const { left, right, up, down } = $settingsStore.outpaintSettings;
        canvas.width = img.width + left + right;
        canvas.height = img.height + up + down;
      } else {
        canvas.width = img.width;
        canvas.height = img.height;
      }

      ctx = canvas.getContext('2d');
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = brushSize;
      ctx.strokeStyle = brushColor;

      if (canvasMode && maskCanvas) {
        maskCanvas.width = canvas.width;
        maskCanvas.height = canvas.height;
        maskCtx = maskCanvas.getContext('2d');
        maskCtx.lineCap = 'round';
        maskCtx.lineJoin = 'round';
        maskCtx.lineWidth = brushSize;
        maskCtx.strokeStyle = brushColor;
        maskCtx.fillStyle = brushColor;
        maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (outpaintMode) {
        drawOutpaintPreview(img);
        // 이미지 확장 모드 기본 줌을 100%로 설정
        $settingsStore.zoomLevel = 100;
        zoomSliderValue = 100;
        centerOnImage();
      } else {
        ctx.drawImage(img, 0, 0);
        if (canvasMode) {
          // 인페인팅/객체 지우기 모드 기본 줌을 100%로 설정
          $settingsStore.zoomLevel = 100;
          zoomSliderValue = 100;
          centerOnImage();
        }
      }

      $imageStore.originalImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    };

    img.src = $imageStore.resultImage || $imageStore.uploadedImageUrl;
  }
  
  function drawOutpaintPreview(img) {
    if (!ctx || !canvas) return;
    
    const { left, right, up, down } = $settingsStore.outpaintSettings;
    const { x, y } = $settingsStore.outpaintSettings.imagePosition;
    
    // 전체 캔버스를 연한 초록색으로 채우기 (그린스크린 느낌)
    ctx.fillStyle = 'rgba(0, 255, 0, 0.2)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // 이미지 위치 계산 (드래그 위치 반영)
    const imgX = left + x;
    const imgY = up + y;
    
    // 이미지 영역 배경을 흰색으로 채우기 (이미지 영역 구분)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillRect(imgX, imgY, img.width, img.height);
    
    // 이미지 그리기
    ctx.drawImage(img, imgX, imgY);
    
    // 이미지 영역에 테두리 표시
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.lineWidth = 2;
    ctx.strokeRect(imgX, imgY, img.width, img.height);
    
    // 확장 영역 표시 (점선)
    ctx.setLineDash([5, 5]);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.strokeRect(0, 0, canvas.width, canvas.height);
    ctx.setLineDash([]);
    
    // 확장 영역에 텍스트 표시
    ctx.font = '14px Arial';
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.textAlign = 'center';
    
    // 최종 해상도 표시
    ctx.fillText(`최종 해상도: ${canvas.width} x ${canvas.height}`, canvas.width / 2, 20);
    
    // 드래그 안내 텍스트
    ctx.fillText('이미지를 드래그하여 위치 조정', canvas.width / 2, canvas.height - 10);
  }
  
  function redrawCanvas() {
    if (!ctx) return;
    
    if (outpaintMode) {
      // 아웃페인트 모드일 경우 확장 영역 다시 그리기
      const img = new Image();
      img.crossOrigin = "Anonymous";
      
      img.onload = () => {
        // 캔버스 크기 재설정
        const { left, right, up, down } = $settingsStore.outpaintSettings;
        canvas.width = img.width + left + right;
        canvas.height = img.height + up + down;
        
        // 컨텍스트 재설정 (캔버스 크기 변경 시 초기화됨)
        ctx = canvas.getContext('2d');
        
        // 확장 영역 미리보기 그리기
        drawOutpaintPreview(img);
      };
      
      img.onerror = (e) => {
        console.error("이미지 다시 로드 실패:", e);
      };
      
      img.src = $imageStore.resultImage || $imageStore.uploadedImageUrl;
    } else if (canvasMode && $imageStore.originalImageData) {
      // 일반 캔버스 모드일 경우 원본 이미지 데이터 복원
      ctx.putImageData($imageStore.originalImageData, 0, 0);
    }
  }
  
  function startDrawing(e) {
    if (!canvasMode || !maskCtx) return;
    
    isDrawing = true;
    
    // 마우스 위치 계산 (줌 레벨 고려)
    const rect = maskCanvas.getBoundingClientRect();
    const scaleX = maskCanvas.width / rect.width;
    const scaleY = maskCanvas.height / rect.height;
    
    // 줌 레벨에 따른 보정
    lastX = (e.clientX - rect.left) * scaleX;
    lastY = (e.clientY - rect.top) * scaleY;

    // 시작점에 점 찍기 (클릭만 했을 때도 점이 찍히도록)
    maskCtx.strokeStyle = brushColor;
    maskCtx.fillStyle = brushColor;
    maskCtx.beginPath();
    maskCtx.arc(lastX, lastY, brushSize / 2, 0, Math.PI * 2);
    maskCtx.fill();
  }
  
  function startDragging(e) {
    if (!outpaintMode || !ctx) return;
    
    isDragging = true;
    
    // 마우스 위치 계산 (줌 레벨 고려)
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    // 줌 레벨에 따른 보정
    dragStartX = (e.clientX - rect.left) * scaleX;
    dragStartY = (e.clientY - rect.top) * scaleY;
  }
  
  function drag(e) {
    if (!isDragging || !outpaintMode || !ctx) return;
    
    // 마우스 위치 계산 (줌 레벨 고려)
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    // 줌 레벨에 따른 보정
    const currentX = (e.clientX - rect.left) * scaleX;
    const currentY = (e.clientY - rect.top) * scaleY;
    
    // 이동 거리 계산
    const deltaX = currentX - dragStartX;
    const deltaY = currentY - dragStartY;
    
    // 이미지 위치 업데이트
    $settingsStore.outpaintSettings.imagePosition.x += deltaX;
    $settingsStore.outpaintSettings.imagePosition.y += deltaY;
    
    // 이미지가 캔버스 영역을 벗어나지 않도록 제한
    const { left, right, up, down } = $settingsStore.outpaintSettings;
    const { x, y } = $settingsStore.outpaintSettings.imagePosition;
    
    // 이미지가 캔버스 영역을 벗어나지 않도록 제한
    if (x < -left) $settingsStore.outpaintSettings.imagePosition.x = -left;
    if (y < -up) $settingsStore.outpaintSettings.imagePosition.y = -up;
    if (x > right) $settingsStore.outpaintSettings.imagePosition.x = right;
    if (y > down) $settingsStore.outpaintSettings.imagePosition.y = down;
    
    // 드래그 시작점 업데이트
    dragStartX = currentX;
    dragStartY = currentY;
    
    // 캔버스 다시 그리기
    redrawCanvas();
  }
  
  function draw(e) {
    if (!isDrawing || !canvasMode || !maskCtx) return;
    
    // 마우스 위치 계산 (줌 레벨 고려)
    const rect = maskCanvas.getBoundingClientRect();
    const scaleX = maskCanvas.width / rect.width;
    const scaleY = maskCanvas.height / rect.height;
    
    // 줌 레벨에 따른 보정
    const currentX = (e.clientX - rect.left) * scaleX;
    const currentY = (e.clientY - rect.top) * scaleY;
    
    maskCtx.beginPath();
    maskCtx.moveTo(lastX, lastY);
    maskCtx.lineTo(currentX, currentY);
    maskCtx.stroke();
    
    lastX = currentX;
    lastY = currentY;
  }
  
  function stopDrawing() {
    if (!isDrawing || !canvasMode) return;
    
    isDrawing = false;
    
    // 마스크 이미지 생성 및 저장
    if (maskCanvas) {
      const maskImage = createMaskImage(maskCanvas);
      $editStore.maskImage = maskImage;
      $imageStore.canvasData = maskImage;
      console.log("마스크 이미지 생성됨");
    }
  }
  
  function stopDragging() {
    if (!isDragging || !outpaintMode) return;
    
    isDragging = false;
  }
  
  function clearCanvas() {
    if (maskCtx && maskCanvas) {
      maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
    }

    if (ctx && canvas && !canvasMode) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 원본 이미지 데이터 복원
      if ($imageStore.originalImageData) {
        ctx.putImageData($imageStore.originalImageData, 0, 0);
      }
    }
    
    // 마스크 이미지 초기화
    $editStore.maskImage = null;
    $imageStore.canvasData = null;
  }
  
  function invertMask() {
    if (!maskCtx || !maskCanvas) return;
    
    // 현재 캔버스 이미지 데이터 가져오기
    const imageData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
    const data = imageData.data;
    
    // 픽셀 단위로 반전 (흰색 <-> 검은색)
    for (let i = 0; i < data.length; i += 4) {
      // RGB 값 반전
      data[i] = 255 - data[i];       // R
      data[i + 1] = 255 - data[i + 1]; // G
      data[i + 2] = 255 - data[i + 2]; // B
      // Alpha 값은 유지
    }
    
    // 반전된 이미지 데이터 적용
    maskCtx.putImageData(imageData, 0, 0);
    
    // 마스크 이미지 업데이트
    const maskImage = createMaskImage(maskCanvas);
    $editStore.maskImage = maskImage;
    $imageStore.canvasData = maskImage;
  }
  
  function setInitialZoom(mode = 'canvas') {
    if (canvasContainer && !userAdjustedZoom) {
      const containerWidth = canvasContainer.clientWidth;
      const containerHeight = canvasContainer.clientHeight;
      let targetWidth, targetHeight;
      
      if (mode === 'canvas') {
        targetWidth = canvas.width;
        targetHeight = canvas.height;
      } else if (mode === 'image') {
        targetWidth = $imageStore.imageWidth;
        targetHeight = $imageStore.imageHeight;
      }
      
      // 줌 레벨 계산 (컨테이너 크기에 맞게)
      const scale = Math.min(containerWidth / targetWidth, containerHeight / targetHeight);
      
      // 인페인팅/객체 지우기 모드일 때는 더 높은 초기 줌 레벨 설정
      if (canvasMode) {
        // 인페인팅/객체 지우기 모드 기본 줌은 이미지가 컨테이너에 맞도록 조정
        $settingsStore.zoomLevel = Math.round(scale * 100);
      } else if (outpaintMode) {
        // 이미지 확장 모드 역시 컨테이너에 맞게 조정
        $settingsStore.zoomLevel = Math.round(scale * 100);
      } else {
        // 기타 모드 기본 줌도 컨테이너에 맞게 설정
        $settingsStore.zoomLevel = Math.round(scale * 100);
      }
      
      zoomSliderValue = $settingsStore.zoomLevel;
      if (mode === 'image') {
        centerOnImage();
      }
    }
  }

  function zoomToImage() {
    if (canvas && canvasContainer && $imageStore.imageWidth && $imageStore.imageHeight && !userAdjustedZoom) {
      const containerWidth = canvasContainer.clientWidth;
      const containerHeight = canvasContainer.clientHeight;
      const imgWidth = $imageStore.imageWidth;
      const imgHeight = $imageStore.imageHeight;
      const scale = Math.min(containerWidth / imgWidth, containerHeight / imgHeight);
      if (outpaintMode) {
        // 이미지 확장 모드에서도 컨테이너 크기에 맞게 줌을 설정
        $settingsStore.zoomLevel = Math.round(scale * 100);
      } else {
        // 다른 모드 역시 컨테이너 크기에 맞게 설정
        $settingsStore.zoomLevel = Math.round(scale * 100);
      }
      zoomSliderValue = $settingsStore.zoomLevel;
      centerOnImage();
    }
  }

  function centerOnImage() {
    if (canvas && canvasContainer) {
      const scale = $settingsStore.zoomLevel / 100;
      let imgX = 0;
      let imgY = 0;

      if (outpaintMode) {
        imgX = $settingsStore.outpaintSettings.left + $settingsStore.outpaintSettings.imagePosition.x;
        imgY = $settingsStore.outpaintSettings.up + $settingsStore.outpaintSettings.imagePosition.y;
      }
      const imgWidth = $imageStore.imageWidth;
      const imgHeight = $imageStore.imageHeight;
      const centerX = (imgX + imgWidth / 2) * scale;
      const centerY = (imgY + imgHeight / 2) * scale;
      const containerWidth = canvasContainer.clientWidth;
      const containerHeight = canvasContainer.clientHeight;
      canvasContainer.scrollLeft = centerX - containerWidth / 2;
      canvasContainer.scrollTop = centerY - containerHeight / 2;
    }
  }

  onMount(() => {
    if (canvasMode || outpaintMode) {
      initCanvas();
    }
  });
  
  afterUpdate(() => {
    if ((canvasMode || outpaintMode) && canvas && canvasContainer) {
      if (!ctx) {
        initCanvas();
      }
    }
  });
</script>

<div class="image-preview" class:no-left-border={hideLeftBorder}>
  <div class="image-controls">
    {#if showZoom}
      <button on:click={zoomOut} class="control-btn">-</button>
      <div class="zoom-slider-container">
        <input
          type="range"
          min="10"
          max="500"
          step="5"
          bind:value={zoomSliderValue}
          class="zoom-slider"
          on:input={() => userAdjustedZoom = true}
        />
        <span class="zoom-level">줌: {zoomSliderValue}%</span>
      </div>
      <button on:click={zoomIn} class="control-btn">+</button>
    {/if}
  </div>

  <div class="image-display">
    {#if canvasMode}
      <div class="brush-controls">
        <label for="brush-size" class="brush-label">
          브러시 크기:
          <input id="brush-size" type="range" min="1" max="50" bind:value={$settingsStore.brushSize} class="brush-slider">
          <span class="brush-size-value">{$settingsStore.brushSize}px</span>
        </label>
        <div class="brush-buttons">
          <button on:click={clearCanvas} class="action-btn">지우기</button>
          <button on:click={invertMask} class="action-btn">마스크 반전</button>
        </div>
      </div>
    {/if}

    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="image-container" style="--zoom-level: {$settingsStore.zoomLevel}" bind:this={canvasContainer} on:click={resetUpdateCounter}>
      {#if canvasMode || outpaintMode}
        <!-- 캔버스 모드 또는 아웃페인트 모드 -->
        <div class="canvas-wrapper">
          <canvas
            bind:this={canvas}
            on:mousedown={!canvasMode ? startDragging : null}
            on:mousemove={!canvasMode ? drag : null}
            on:mouseup={!canvasMode ? stopDragging : null}
            on:mouseleave={!canvasMode ? stopDragging : null}
            class="edit-canvas"
            class:outpaint-canvas={outpaintMode}
          ></canvas>
          {#if canvasMode}
            <canvas
              bind:this={maskCanvas}
              class="mask-canvas"
              on:mousedown={startDrawing}
              on:mousemove={draw}
              on:mouseup={stopDrawing}
              on:mouseleave={stopDrawing}
            ></canvas>
          {/if}
        </div>
        <div class="canvas-instructions">
          {#if $editStore.selectedFeature.id === 'inpaint'}
            <p>인페인팅할 영역을 그려주세요<br>(마우스 좌클릭 후 드래그)</p>
          {:else if $editStore.selectedFeature.id === 'erase_object'}
            <p>지울 객체를 그려주세요 (마우스 좌클릭 후 드래그)</p>
          {:else if $editStore.selectedFeature.id === 'outpaint'}
            <p>이미지를 드래그하여 위치를 조정할 수 있습니다</p>
          {/if}
        </div>
      {:else}
        <!-- 일반 이미지 모드 -->
        {#if displayedImageUrl}
          {#if isVideoResult}
            <!-- svelte-ignore a11y-media-has-caption -->
            <video
              controls
              src={constructUrl(displayedImageUrl)}
              class="edit-video"
            >동영상을 재생할 수 없습니다.</video>
          {:else}
          <img
            src={constructUrl(displayedImageUrl)}
            alt="편집 중인 이미지"
            class="edit-image"
            on:load={() => console.log("편집 탭 이미지 로드됨:", constructUrl(displayedImageUrl))}
            on:error={(e) => {
              console.error("편집 탭 이미지 로드 오류:", e);
              console.log("시도한 URL:", constructUrl(displayedImageUrl));
            }}
            crossorigin="anonymous"
          />
          {/if}
        {:else}
          <div class="no-image">
            <p>이미지가 없습니다. 이미지를 업로드해주세요.</p>
          </div>
        {/if}
      {/if}
    </div>
  </div>

  <div class="image-info">
    <span>해상도: {$imageStore.imageWidth || 0} x {$imageStore.imageHeight || 0}</span>
    {#if outpaintMode}
      <span>확장 후 해상도: {($imageStore.imageWidth || 0) + $settingsStore.outpaintSettings.left + $settingsStore.outpaintSettings.right} x {($imageStore.imageHeight || 0) + $settingsStore.outpaintSettings.up + $settingsStore.outpaintSettings.down}</span>
    {/if}
  </div>
</div>

<style>
  .image-preview {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 0;
    border-left: 1px solid #dfe3e8;
  }

  .image-preview.no-left-border {
    border-left: none;
  }

  .image-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
    position: relative;
    z-index: 1;
    padding: 0.8rem;
    margin-bottom: 0.5rem;
  }

  .image-display {
    flex: 1;
    display: flex;
  }

  .edit-video {
    width: 100%;
    max-width: 100%;
    max-height: 57vh;
    object-fit: contain;
  }

  :global(.dark) .image-controls {
    background-color: transparent;
  }

  :global(.light) .image-controls {
    background-color: transparent;
  }
  
  .zoom-slider-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 150px;
  }
  
  .zoom-slider {
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: linear-gradient(to right, #555, #999);
    -webkit-appearance: none;
    appearance: none;
    outline: none;
  }
  
  .zoom-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #4a90e2;
    cursor: pointer;
  }
  
  .zoom-level {
    font-size: 0.8rem;
    margin-top: 0.3rem;
    color: var(--text-color, #1d1d1d);
  }

  :global(.dark) .zoom-level {
    color: var(--text-color, #ececec);
  }
  
  .control-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background-color: rgba(255, 255, 255, 0.1);
    color: var(--text-color, #f0f0f0);
    font-size: 1.2rem;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .control-btn:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  :global(.light) .control-btn {
    background-color: rgba(0, 0, 0, 0.05);
    color: #404040;
  }

  :global(.light) .control-btn:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
  
  .brush-controls {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    margin-right: 1rem;
  }
  
  .brush-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8rem;
    line-height: 1;
    white-space: nowrap;
    color: var(--text-color, #f0f0f0);
  }

  :global(.light) .brush-label {
    color: #404040;
  }
  
  .brush-slider {
    width: 100px;
    height: 4px;
    border-radius: 2px;
    background: linear-gradient(to right, #555, #999);
    -webkit-appearance: none;
    appearance: none;
    outline: none;
  }
  
  .brush-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #4a90e2;
    cursor: pointer;
  }
  
  .brush-size-value {
    font-size: 0.75rem;
    min-width: 40px;
  }

  :global(.light) .brush-size-value {
    color: #404040;
  }
  
  .brush-buttons {
    display: flex;
    gap: 0.5rem;
  }

  .action-btn {
    padding: 0.4rem 0.8rem;
    border-radius: 4px;
    border: none;
    background-color: rgba(255, 255, 255, 0.1);
    color: var(--text-color, #f0f0f0);
    font-size: 0.8rem;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .action-btn:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  :global(.light) .action-btn {
    background-color: rgba(0, 0, 0, 0.05);
    color: #404040;
  }

  :global(.light) .action-btn:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
  
  .image-container {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: auto;
    position: relative;
    width: 100%;
    height: auto;
    max-height: 70vh;
    margin: 0 auto;
  }

  :global(.light) .image-container {
    background-color: transparent;
  }
  
  .edit-image {
    max-width: 100%;
    max-height: 70vh;
    width: auto;
    height: auto;
    object-fit: contain;
    transform: scale(calc(var(--zoom-level) / 100));
    transform-origin: center;
    transition: all 0.3s ease;
  }
  
  .edit-canvas {
    max-width: 100%;
    max-height: 70vh;
    width: auto;
    height: auto;
    display: block;
    transform: scale(calc(var(--zoom-level) / 100));
    transform-origin: center;
    transition: all 0.3s ease;
  }
  
  .outpaint-canvas {
    cursor: move; /* 드래그 가능함을 나타내는 커서 */
  }

  .canvas-wrapper {
    position: relative;
    display: inline-flex;
    justify-content: center;
    align-items: center;
  }

  .mask-canvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 10;
    opacity: 0.8;
    transform: scale(calc(var(--zoom-level) / 100));
    transform-origin: center;
  }
  
  .no-image {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    color: var(--text-color, #f0f0f0);
    opacity: 0.5;
  }
  
  .canvas-instructions {
    position: absolute;
    bottom: 1rem;
    left: 50%;
    transform: translateX(-50%);
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.9rem;
    pointer-events: none;
  }
  
  .image-info {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem;
    font-size: 0.9rem;
    color: white;
    opacity: 0.8;
  }

  :global(.light) .image-info {
    color: black;
  }
  @media (max-width: 800px) {
    .image-display, .image-container { max-height: 45dvh; }
    .image-container img { max-height: 40dvh; }
  }
</style>
